"use client";

import React, { useState } from "react";
import {
  Box,
  Button,
  Typography,
  CircularProgress,
  Stack,
  Tooltip,
  useMediaQuery,
} from "@mui/material";
import { AutoAwesome } from "@mui/icons-material";
import ConfirmDialog from "@/app/components/modals/ConfirmDialog";
import { useTheme } from "@mui/material/styles";

const ReviewWritingAssistant = ({
  currentReview,
  onUseContent,
  rating,
  sharerName,
  meetingSummary = null,
}) => {
  const [isGenerating, setIsGenerating] = useState(false);
  const [suggestions, setSuggestions] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const getPrompt = () => {
    const hasContent = currentReview && currentReview.trim().length > 0;
    const hasSummary = meetingSummary && meetingSummary.trim().length > 0;

    if (hasContent) {
      // Improve existing review
      return `Improve and refine this meeting review:

"${currentReview}"

Rating given: ${rating} out of 5 stars
Meeting with: ${sharerName}
${hasSummary ? `Meeting summary: ${meetingSummary}` : ""}

Write 2 different improved versions that:
- Keep the same core message and sentiment
- Sound more natural and authentic
- Are polished and professional
- Stay under 500 characters each
- Match the ${rating}-star rating tone

Only improve the existing review. Do not add information not mentioned.`;
    }

    // Generate from scratch
    if (hasSummary) {
      return `Write a thoughtful meeting review based on this meeting summary:

Meeting with: ${sharerName}
Rating: ${rating} out of 5 stars
Meeting summary: ${meetingSummary}

Write 2 different review options that:
- Reference specific topics or insights from the meeting
- Sound authentic and conversational
- Match the ${rating}-star rating sentiment
- Stay under 500 characters each
- Feel genuine and personal

Focus on what made the meeting valuable and what was appreciated.`;
    }

    // No summary available
    return `Write a thoughtful meeting review for this context:

Meeting with: ${sharerName}
Rating: ${rating} out of 5 stars

Write 2 different review options that:
- Sound authentic and conversational
- Match the ${rating}-star rating sentiment (${getRatingDescription(rating)})
- Stay under 500 characters each
- Feel genuine and personal
- Focus on professional value and insights

Provide helpful, specific appreciation appropriate for a ${rating}-star meeting.`;
  };

  const getRatingDescription = (rating) => {
    const descriptions = {
      1: "Poor - significant issues",
      2: "Fair - below expectations",
      3: "Good - met expectations",
      4: "Very Good - exceeded expectations",
      5: "Excellent - outstanding experience",
    };
    return descriptions[rating] || "Good";
  };

  const cleanSuggestionText = (text) => {
    return text
      .replace(/^\d+\.?\s*/, "")
      .replace(/^[^:]*:\s*/, "")
      .replace(/^["']|["']$/g, "")
      .replace(/^"|"$/g, "")
      .replace(/^'|'$/g, "")
      .trim();
  };

  const generateSuggestions = async () => {
    if (rating === 0) {
      return; // Don't generate without a rating
    }

    setIsGenerating(true);
    const hasExistingContent = currentReview && currentReview.trim().length > 0;

    try {
      const response = await fetch("/api/ai/review-writer", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          prompt: getPrompt(),
          rating,
          isImproving: hasExistingContent,
          sharerName,
          meetingSummary: meetingSummary || null,
        }),
      });

      if (!response.ok) throw new Error("Failed to generate suggestions");

      const reader = response.body?.getReader();
      const decoder = new TextDecoder();
      let content = "";

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        content += decoder.decode(value);
      }

      const newSuggestions = parseAIResponse(content);
      const finalSuggestions = newSuggestions.slice(0, 2);

      if (finalSuggestions.length === 0) {
        const fallbackSuggestions = [content.trim()];
        setSuggestions(fallbackSuggestions);
      } else {
        setSuggestions(finalSuggestions);
      }

      setShowModal(true);
    } catch (error) {
      setSuggestions([
        "Sorry, I couldn't generate suggestions right now. Please try again.",
      ]);
      setShowModal(true);
    } finally {
      setIsGenerating(false);
    }
  };

  const parseAIResponse = (content) => {
    const suggestions = [];

    const lines = content.split("\n").filter((line) => line.trim());

    lines.forEach((line) => {
      if (line.match(/^\d+\.?\s+/)) {
        const cleanedText = cleanSuggestionText(line);
        if (
          cleanedText &&
          cleanedText.length > 10 &&
          cleanedText.length <= 500
        ) {
          suggestions.push(cleanedText);
        }
      } else if (line.includes(":") && line.length > 20) {
        const cleanedText = cleanSuggestionText(line);
        if (
          cleanedText &&
          cleanedText.length > 10 &&
          cleanedText.length <= 500
        ) {
          suggestions.push(cleanedText);
        }
      }
    });

    if (suggestions.length === 0) {
      const paragraphs = content.split("\n\n").filter((p) => p.trim());
      paragraphs.forEach((paragraph) => {
        const cleanedText = cleanSuggestionText(paragraph);
        if (
          cleanedText &&
          cleanedText.length > 10 &&
          cleanedText.length <= 500
        ) {
          suggestions.push(cleanedText);
        }
      });
    }

    return suggestions;
  };

  const handleSelectSuggestion = (suggestion) => {
    onUseContent(suggestion);
    setShowModal(false);
  };

  const handleRegenerateInModal = async () => {
    setIsGenerating(true);
    const hasExistingContent = currentReview && currentReview.trim().length > 0;

    try {
      const response = await fetch("/api/ai/review-writer", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          prompt: getPrompt(),
          rating,
          isImproving: hasExistingContent,
          sharerName,
          meetingSummary: meetingSummary || null,
        }),
      });

      if (!response.ok) throw new Error("Failed to generate suggestions");

      const reader = response.body?.getReader();
      const decoder = new TextDecoder();
      let content = "";

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        content += decoder.decode(value);
      }

      const newSuggestions = parseAIResponse(content);
      const finalSuggestions = newSuggestions.slice(0, 2);

      if (finalSuggestions.length === 0) {
        setSuggestions([
          "Unable to generate new suggestions. Please try again.",
        ]);
      } else {
        setSuggestions(finalSuggestions);
      }
    } catch (error) {
      setSuggestions(["Error generating suggestions. Please try again."]);
    } finally {
      setIsGenerating(false);
    }
  };

  const modalBody = (
    <Box sx={{ pt: 0.5 }}>
      <Typography
        sx={{
          color: "text.secondary",
          fontSize: "0.875rem",
          lineHeight: 1.6,
          mb: 2.5,
        }}
      >
        {suggestions.length
          ? "Choose a suggestion to use it. You can edit it afterward."
          : "No suggestions yet. Try again to create a draft."}
      </Typography>
      <Stack spacing={1.5} aria-busy={isGenerating}>
        {suggestions.map((suggestion, index) => (
          <Box
            key={index}
            component="button"
            type="button"
            disabled={isGenerating}
            onClick={() => handleSelectSuggestion(suggestion)}
            sx={{
              p: 2.5,
              width: "100%",
              textAlign: "left",
              font: "inherit",
              color: "text.primary",
              backgroundColor: "transparent",
              border: "1px solid",
              borderColor: "divider",
              borderRadius: "10px",
              cursor: "pointer",
              transition: "border-color 0.15s ease",
              "&:hover, &:focus-visible": { borderColor: "text.primary" },
              "&:focus-visible": {
                outline: "2px solid",
                outlineColor: "text.primary",
                outlineOffset: 3,
              },
              "&:disabled": { opacity: 0.5, cursor: "default" },
            }}
          >
            <Typography
              component="span"
              sx={{
                display: "block",
                fontSize: "0.9375rem",
                lineHeight: 1.6,
                whiteSpace: "pre-wrap",
              }}
            >
              {suggestion}
            </Typography>
            <Box
              component="span"
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                gap: 2,
                mt: 2,
              }}
            >
              <Typography
                component="span"
                sx={{ fontSize: "0.75rem", color: "text.secondary" }}
              >
                {suggestion.length} characters
              </Typography>
              <Typography
                component="span"
                sx={{ fontSize: "0.875rem", fontWeight: 600 }}
              >
                Use this draft
              </Typography>
            </Box>
          </Box>
        ))}
      </Stack>
      <Box sx={{ mt: 2.5, display: "flex", justifyContent: "flex-end" }}>
        <Button
          variant="text"
          onClick={handleRegenerateInModal}
          disabled={isGenerating}
          startIcon={
            isGenerating ? (
              <CircularProgress size={16} color="inherit" />
            ) : (
              undefined
            )
          }
          sx={{
            textTransform: "none",
            color: "text.primary",
            fontWeight: 600,
            minHeight: 44,
            px: 1.5,
            backgroundColor: "transparent",
            "&:hover, &.Mui-disabled": { backgroundColor: "transparent" },
          }}
        >
          {isGenerating ? "Generating…" : "Try again"}
        </Button>
      </Box>
    </Box>
  );

  const hasContent = currentReview && currentReview.trim().length > 0;

  return (
    <>
      <Tooltip
        title={
          rating === 0 ? "Select a star rating first to draft with AI" : ""
        }
      >
        <Box>
          <Button
            aria-label={hasContent ? "Improve with AI" : "Draft with AI"}
            variant="text"
            onClick={generateSuggestions}
            disabled={isGenerating || rating === 0}
            startIcon={
              <Box
                sx={{
                  width: 20,
                  height: 20,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                {isGenerating ? (
                  <CircularProgress size={16} color="primary" />
                ) : (
                  <AutoAwesome sx={{ fontSize: 20, color: "primary.main" }} />
                )}
              </Box>
            }
            sx={{
              textTransform: "none",
              color: rating === 0 ? "text.disabled" : "text.secondary",
              backgroundColor: "transparent",
              minWidth: isMobile ? "auto" : undefined,
              "&:hover": {
                backgroundColor: "transparent",
                color: rating === 0 ? "text.disabled" : "text.primary",
              },
            }}
          >
            {!isMobile &&
              (isGenerating
                ? "Generating..."
                : hasContent
                ? "Improve with AI"
                : "Draft with AI")}
          </Button>
        </Box>
      </Tooltip>
      <ConfirmDialog
        open={showModal}
        onClose={() => setShowModal(false)}
        title={hasContent ? "Improved review" : "Review suggestions"}
        body={modalBody}
        showConfirmButton={false}
        ignoreButtonText="Close"
        showIgnoreButton={false}
        modalStyle="center"
        backdropStyle="frosted"
        customStyle={{
          width: "min(620px, calc(100vw - 48px))",
          maxHeight: "calc(100dvh - 48px)",
          borderRadius: "10px",
          px: 0,
          overflowY: "auto",
          display: "flex",
          flexDirection: "column",
        }}
        contentSx={{ px: 4, pt: 4, pb: 1.5 }}
        titleSx={{
          fontSize: "1.625rem",
          fontWeight: 600,
          lineHeight: 1.25,
          letterSpacing: "-0.5px",
        }}
        bodySx={{ mb: 0 }}
        mobileContentSx={{ borderRadius: "10px 10px 0 0" }}
        disableBackdropClick={false}
        showCloseIcon={true}
      />
    </>
  );
};

export default ReviewWritingAssistant;
